/**
 * Página de autenticação.
 * Estados: login | otp (2º fator) | forgot | forgot-sent
 * Porta o design de screen-auth.jsx.
 *
 * O primeiro acesso por convite NÃO mora aqui: ele acontece em /invitations/accept, tela própria que recebe o token do e-mail.
 * O formulário de ativação que existia nesta página era uma maquete — e-mail fixo em código e token literal — e foi removido junto com a chegada do fluxo real, para ninguém ser levado a uma ativação que não ativa nada.
 */

import { useState, useId } from "react";
import { useLocation } from "react-router-dom";
import { Icon, AzimWordmark, AzimGlyph } from "@azim-crm/icons";
import { Button, Input, Field } from "@azim-crm/ui-components";
import { useAuth } from "../../app/AuthProvider.js";
import styles from "./AuthPage.module.css";

type AuthMode = "login" | "otp" | "forgot" | "forgot-sent";

// ---- Brand panel ---------------------------------------------------------
function BrandPanel({ mode }: { mode: "login" | "forgot" | "forgot-sent" }) {
  const copy: Record<string, [string, string]> = {
    login: ["Seu norte comercial.", "Entre para acompanhar contas, oportunidades e o pipeline com direção."],
    forgot: ["Vamos te reorientar.", "Enviamos um link seguro para você redefinir a senha e voltar à rota."],
    "forgot-sent": ["Vamos te reorientar.", "Enviamos um link seguro para você redefinir a senha e voltar à rota."],
  };
  const [heading, description] = copy[mode] ?? copy["login"] ?? ["", ""];

  return (
    <div className={styles.brand} aria-hidden="true">
      <div className={styles.brandGlyph}>
        <AzimGlyph ring="#FFFFFF" needle="var(--vellus-lagoa)" tail="rgba(255,255,255,0.4)" />
      </div>
      <div className={styles.brandLogo}>
        <AzimWordmark size={24} variant="tile" byline color="#fff" />
      </div>
      <div className={styles.brandContent}>
        <div className={styles.brandPill}>
          <Icon name="target" size={14} />
          CRM corporativo · Vellus
        </div>
        <h1 className={styles.brandTitle}>{heading}</h1>
        <p className={styles.brandDescription}>{description}</p>
      </div>
      <footer className={styles.brandFooter}>
        <span className={styles.brandFooterItem}>
          <Icon name="shieldCheck" size={15} /> PCI DSS 4.0.1
        </span>
        <span className={styles.brandFooterItem}>
          <Icon name="lock" size={15} /> Acesso por convite
        </span>
        <span>© 2026 Vellus</span>
      </footer>
    </div>
  );
}

// ---- Error box -----------------------------------------------------------
function ErrorBox({ message }: { message: string }) {
  return (
    <div className={styles.errorBox} role="alert">
      <Icon name="alertTriangle" size={16} style={{ flexShrink: 0 }} />
      {message}
    </div>
  );
}

// ---- Back link -----------------------------------------------------------
function BackLink({ onClick }: { onClick(): void }) {
  return (
    <button type="button" className={styles.backLink} onClick={onClick}>
      <Icon name="chevronLeft" size={15} />
      Voltar ao login
    </button>
  );
}

// ---- Login form ----------------------------------------------------------
function LoginForm({
  onForgot,
  onOtpRequired,
}: {
  onForgot(): void;
  /** Chamado quando a 1ª etapa exige segundo fator (2FA por e-mail). */
  onOtpRequired(challengeId: string, email: string): void;
}) {
  const { login, isLoading } = useAuth();
  const location = useLocation();
  // O aceite de convite redireciona para cá com o e-mail no state do router, para o convidado não ter de redigitá-lo no primeiro acesso.
  const prefilledEmail = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const emailId = useId();
  const passwordId = useId();

  function fillDemo() {
    // Credencial de bootstrap do IdP self-hosted (ADR-0017), semeada no stack local.
    setEmail("admin@azim.dev");
    setPassword("Azim@Dev2026");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Informe e-mail e senha.");
      return;
    }
    try {
      const outcome = await login(email, password);
      // 2FA habilitado: avança para a tela de código (o código foi enviado por e-mail).
      if (outcome.requiresOtp) {
        onOtpRequired(outcome.challengeId, email.trim());
      }
    } catch (err) {
      // Anti-enumeração (Req 1.4, WEB-ERR-001): mensagem genérica do AuthProvider.
      const msg = err instanceof Error ? err.message : "Credenciais inválidas. Verifique e tente novamente.";
      setError(msg);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.formTitle}>Entrar no Azim</h2>
      <p className={styles.formSubtitle}>Use as credenciais da sua conta Vellus.</p>

      {error && <ErrorBox message={error} />}

      <div style={{ marginBottom: 14 }}>
        <Field label="E-mail corporativo" htmlFor={emailId}>
          <Input
            id={emailId}
            icon="mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@empresa.com"
            autoFocus
            autoComplete="email"
            required
          />
        </Field>
      </div>

      <div className={styles.fieldGroup}>
        <div className={styles.fieldRow}>
          <label htmlFor={passwordId} className={styles.fieldLabel}>
            Senha
          </label>
          <button type="button" className={styles.linkButton} onClick={onForgot}>
            Esqueci minha senha
          </button>
        </div>
        <Input
          id={passwordId}
          icon="lock"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          autoComplete="current-password"
          required
          rightEl={
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              aria-pressed={showPassword}
              title={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              <Icon name={showPassword ? "eyeOff" : "eye"} size={16} />
            </button>
          }
        />
      </div>

      <Button
        type="submit"
        variant="primary"
        full
        size="lg"
        disabled={isLoading}
        {...(!isLoading && { iconRight: "arrowRight" as const })}
      >
        {isLoading ? "Entrando…" : "Entrar"}
      </Button>

      <button type="button" className={`${styles.linkButton} ${styles.linkButtonBlock}`} onClick={fillDemo}>
        Preencher credenciais de demonstração
      </button>

      <div className={styles.divider} aria-hidden="true">
        <span className={styles.dividerLine} />
      </div>

      <div className={styles.infoBlock}>
        <span className={styles.infoIcon}>
          <Icon name="shieldAlert" size={17} />
        </span>
        <div className={styles.infoText}>
          <strong>Acesso somente por convite.</strong> Não há cadastro aberto nem login social. Quem foi convidado recebe por e-mail um link de ativação que leva direto à definição da senha — a ativação acontece por ele, não por esta tela.
        </div>
      </div>
    </form>
  );
}

// ---- OTP form (2º fator por e-mail) --------------------------------------
function OtpForm({
  challengeId,
  email,
  onBack,
}: {
  challengeId: string;
  email: string;
  onBack(): void;
}) {
  const { verifyOtp, isLoading } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const codeId = useId();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Informe o código de 6 dígitos.");
      return;
    }
    try {
      await verifyOtp(challengeId, trimmed);
    } catch (err) {
      // Mensagem pt-BR mapeada pelo adapter (código inválido/expirado, lockout).
      const msg =
        err instanceof Error ? err.message : "Código inválido ou expirado.";
      setError(msg);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.formTitle}>Verificação em duas etapas</h2>
      <p className={styles.formSubtitle}>
        Enviamos um código de 6 dígitos para <strong>{email}</strong>. Informe-o abaixo
        para concluir o login. O código expira em 5 minutos.
      </p>

      {error && <ErrorBox message={error} />}

      <div style={{ marginBottom: 20 }}>
        <Field label="Código de verificação" htmlFor={codeId}>
          <Input
            id={codeId}
            icon="shieldCheck"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            autoFocus
            required
            aria-describedby={`${codeId}-hint`}
          />
        </Field>
        <span id={`${codeId}-hint`} className={styles.formSubtitle} style={{ fontSize: 12 }}>
          Não recebeu? Verifique a caixa de spam ou volte e tente novamente.
        </span>
      </div>

      <Button
        type="submit"
        variant="primary"
        full
        size="lg"
        disabled={isLoading}
        {...(!isLoading && { iconRight: "arrowRight" as const })}
      >
        {isLoading ? "Verificando…" : "Verificar e entrar"}
      </Button>

      <BackLink onClick={onBack} />
    </form>
  );
}

// ---- Forgot form ---------------------------------------------------------
function ForgotForm({ onBack }: { onBack(): void }) {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const emailId = useId();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    await requestPasswordReset(email);
    setSent(true);
  }

  if (sent) {
    return (
      <div className={styles.form}>
        <div className={styles.successIcon} aria-hidden="true">
          <Icon name="check" size={26} />
        </div>
        <h2 className={styles.formTitle}>Verifique seu e-mail</h2>
        <p className={styles.formSubtitle} style={{ marginBottom: 24 }}>
          Se houver uma conta para <strong>{email}</strong>, enviamos um link para redefinir a
          senha. O link expira em 30 minutos.
        </p>
        <Button variant="secondary" full icon="chevronLeft" onClick={onBack}>
          Voltar ao login
        </Button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.formTitle}>Redefinir senha</h2>
      <p className={styles.formSubtitle}>
        Informe seu e-mail e enviaremos um link seguro de redefinição.
      </p>

      <div style={{ marginBottom: 20 }}>
        <Field label="E-mail corporativo" htmlFor={emailId}>
          <Input
            id={emailId}
            icon="mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@empresa.com"
            autoFocus
            autoComplete="email"
            required
          />
        </Field>
      </div>

      <Button type="submit" variant="primary" full size="lg" icon="send">
        Enviar link de redefinição
      </Button>

      <BackLink onClick={onBack} />
    </form>
  );
}

// ---- AuthPage (exported) -------------------------------------------------
export function AuthPage() {
  const [mode, setMode] = useState<AuthMode>("login");
  // Desafio de 2º fator: preenchido quando a 1ª etapa exige OTP (2FA por e-mail).
  const [otpChallengeId, setOtpChallengeId] = useState("");
  const [otpEmail, setOtpEmail] = useState("");

  // O BrandPanel só conhece login/forgot — mapeia 'otp' para 'login'.
  const brandMode = mode === "forgot-sent" ? "forgot" : mode === "otp" ? "login" : mode;

  return (
    <div className={styles.container}>
      <BrandPanel mode={brandMode} />
      <div className={styles.formPanel}>
        {mode === "login" && (
          <LoginForm
            onForgot={() => setMode("forgot")}
            onOtpRequired={(challengeId, email) => {
              setOtpChallengeId(challengeId);
              setOtpEmail(email);
              setMode("otp");
            }}
          />
        )}
        {mode === "otp" && (
          <OtpForm
            challengeId={otpChallengeId}
            email={otpEmail}
            onBack={() => setMode("login")}
          />
        )}
        {(mode === "forgot" || mode === "forgot-sent") && (
          <ForgotForm onBack={() => setMode("login")} />
        )}
      </div>
    </div>
  );
}
